import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  ShoppingBag as OrdersIcon,
  Inventory as ProductsIcon,
  People as CustomersIcon,
  LocalOffer as CouponsIcon,
  Category as CategoriesIcon,
  Tune as AttributesIcon,
  PhotoLibrary as GalleryIcon,
  Article as BlogIcon,
  Description as PagesIcon,
  Star as ReviewsIcon,
  BarChart as ReportsIcon,
  CalendarMonth as CalendarIcon,
  Notifications as PushIcon,
  Settings as SettingsIcon,
  Translate as TranslateIcon,
  Menu as MenuIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';

const DRAWER_WIDTH = 240;

const NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/admin', exact: true },
  { label: 'Orders', icon: <OrdersIcon />, path: '/admin/orders' },
  { label: 'Products', icon: <ProductsIcon />, path: '/admin/products' },
  { label: 'Customers', icon: <CustomersIcon />, path: '/admin/customers' },
  { label: 'Coupons', icon: <CouponsIcon />, path: '/admin/coupons' },
  { label: 'Categories', icon: <CategoriesIcon />, path: '/admin/categories' },
  { label: 'Attributes', icon: <AttributesIcon />, path: '/admin/attributes' },
  { label: 'Gallery', icon: <GalleryIcon />, path: '/admin/gallery' },
  { label: 'Blog', icon: <BlogIcon />, path: '/admin/blog' },
  { label: 'Pages', icon: <PagesIcon />, path: '/admin/pages' },
  { label: 'Reviews', icon: <ReviewsIcon />, path: '/admin/reviews' },
  { label: 'Reports', icon: <ReportsIcon />, path: '/admin/reports' },
  { label: 'Calendar', icon: <CalendarIcon />, path: '/admin/calendar' },
  { label: 'Push', icon: <PushIcon />, path: '/admin/push-notifications' },
  { label: 'Translations', icon: <TranslateIcon />, path: '/admin/translations' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
];

const LINKABLE_BREADCRUMB_PATHS = new Set([
  ...NAV_ITEMS.map((item) => item.path),
  '/admin/variations',
  '/admin/messaging',
]);

const SEGMENT_LABELS = {
  admin: 'Admin',
  orders: 'Orders',
  products: 'Products',
  customers: 'Customers',
  coupons: 'Coupons',
  categories: 'Categories',
  attributes: 'Attributes',
  gallery: 'Gallery',
  blog: 'Blog',
  pages: 'Pages',
  reviews: 'Reviews',
  reports: 'Reports',
  calendar: 'Calendar',
  'push-notifications': 'Push Notifications',
  settings: 'Settings',
  translations: 'Translations',
  new: 'New',
  create: 'Create',
  edit: 'Edit',
  send: 'Send',
  analytics: 'Analytics',
  device: 'Device',
  parent: 'Parent',
  variations: 'Variations',
  messaging: 'Messaging',
};

function buildBreadcrumbs(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs = [];
  let accumulated = '';
  parts.forEach((part, i) => {
    accumulated += `/${part}`;
    if (/^\d+$/.test(part)) return;

    const label = SEGMENT_LABELS[part] || decodeURIComponent(part);
    crumbs.push({
      label,
      path: accumulated,
      canLink: LINKABLE_BREADCRUMB_PATHS.has(accumulated),
    });
  });

  return crumbs.map((crumb, index) => ({
    ...crumb,
    isLast: index === crumbs.length - 1,
  }));
}

function isNavItemActive(item, pathname) {
  if (item.exact) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(item.path + '/');
}

function NavList({ pathname, onNavigate }) {
  const navigate = useNavigate();

  const handleClick = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  return (
    <List disablePadding>
      {NAV_ITEMS.map((item) => {
        const active = isNavItemActive(item, pathname);
        return (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleClick(item.path)}
              selected={active}
              sx={{
                borderRadius: 1,
                mx: 1,
                my: 0.25,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

function DrawerContent({ pathname, onNavigate }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2 }}>
        <Typography variant="h6" noWrap fontWeight={700} color="primary.main">
          Admin
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <NavList pathname={pathname} onNavigate={onNavigate} />
      </Box>
    </Box>
  );
}

const AdminLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;

  const breadcrumbs = buildBreadcrumbs(pathname);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleMobileClose = () => setMobileOpen(false);

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
      {/* Desktop permanent sidebar */}
      <Box
        component="nav"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box
          sx={{
            width: DRAWER_WIDTH,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <DrawerContent pathname={pathname} />
        </Box>
      </Box>

      {/* Mobile temporary drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <DrawerContent pathname={pathname} onNavigate={handleMobileClose} />
      </Drawer>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pl: { xs: 0, md: 2 },
        }}
      >
        {/* Mobile header with hamburger */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            mb: 1,
            gap: 1,
          }}
        >
          <Tooltip title="Open menu">
            <IconButton onClick={handleDrawerToggle} size="small">
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle2" color="text.secondary">
            Admin
          </Typography>
        </Box>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 1 && (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 2, fontSize: 13 }}
          >
            {breadcrumbs.map((crumb) =>
              crumb.isLast || !crumb.canLink ? (
                <Typography key={crumb.path} fontSize={13} color="text.primary">
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path}
                  component={RouterLink}
                  to={crumb.path}
                  underline="hover"
                  color="inherit"
                  sx={{ fontSize: 13 }}
                >
                  {crumb.label}
                </Link>
              )
            )}
          </Breadcrumbs>
        )}

        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout;
export { buildBreadcrumbs };
